/** @jsxImportSource @emotion/react */
import { Box, TextField, Button, IconButton, useTheme, Tabs, Tab, Divider } from '@mui/material'
import { Add, Delete, ArrowUpward, ArrowDownward } from '@mui/icons-material'
import { Chapter, Step } from '@app/components/Risk/types'
import { formSectionStyles, formLabelStyles } from './StoryBuilder.styles'
import { StepEditor } from './StepEditor'
import { createEmptyStep } from './types/localStory.types'
import { useState } from 'react'

interface ChapterEditorProps {
  chapter: Chapter
  chapterIndex: number
  onUpdate: (updates: Partial<Chapter>) => void
  onDelete: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  canDelete: boolean
  canMoveUp: boolean
  canMoveDown: boolean
}

export const ChapterEditor = ({
  chapter,
  chapterIndex,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  canDelete,
  canMoveUp,
  canMoveDown,
}: ChapterEditorProps) => {
  const theme = useTheme()
  const [selectedStepIndex, setSelectedStepIndex] = useState(0)

  const handleAddStep = () => {
    const newSteps = [...chapter.steps, createEmptyStep()]
    onUpdate({ steps: newSteps })
    setSelectedStepIndex(newSteps.length - 1)
  }

  const handleUpdateStep = (stepIndex: number, updates: Partial<Step>) => {
    const updatedSteps = [...chapter.steps]
    updatedSteps[stepIndex] = {
      ...updatedSteps[stepIndex],
      ...updates,
    }
    onUpdate({ steps: updatedSteps })
  }

  const handleDeleteStep = (stepIndex: number) => {
    if (chapter.steps.length <= 1) return
    const newSteps = chapter.steps.filter((_, i) => i !== stepIndex)
    onUpdate({ steps: newSteps })
    if (selectedStepIndex >= newSteps.length) {
      setSelectedStepIndex(newSteps.length - 1)
    }
  }

  const handleMoveStep = (stepIndex: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1
    if (newIndex < 0 || newIndex >= chapter.steps.length) return

    const newSteps = [...chapter.steps]
    const [movedStep] = newSteps.splice(stepIndex, 1)
    newSteps.splice(newIndex, 0, movedStep)
    onUpdate({ steps: newSteps })
    setSelectedStepIndex(newIndex)
  }

  return (
    <Box>
      <Box css={formSectionStyles(theme)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Chapter {chapterIndex + 1}</Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={onMoveUp} disabled={!canMoveUp}>
              <ArrowUpward fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onMoveDown} disabled={!canMoveDown}>
              <ArrowDownward fontSize="small" />
            </IconButton>
            {canDelete && (
              <IconButton size="small" onClick={onDelete} color="error">
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box css={formLabelStyles(theme)}>Chapter Title</Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter chapter title"
            value={chapter.title || ''}
            onChange={(e) => onUpdate({ title: e.target.value })}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box css={formLabelStyles(theme)}>Chapter Description</Box>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            placeholder="Brief description of this chapter"
            value={chapter.description || ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
          />
        </Box>
      </Box>

      <Divider />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
        <Tabs
          value={selectedStepIndex}
          onChange={(_, newValue) => setSelectedStepIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flex: 1 }}
        >
          {chapter.steps.map((step, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Step {index + 1}</span>
                  {step.title && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                        maxWidth: 80,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {step.title}
                    </Box>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={handleAddStep}
          sx={{ mx: 1, flexShrink: 0 }}
        >
          Step
        </Button>
      </Box>

      {chapter.steps.map((step, stepIndex) => (
        <Box key={stepIndex} sx={{ display: selectedStepIndex === stepIndex ? 'block' : 'none' }}>
          <Box sx={{ p: 1, bgcolor: theme.palette.grey[50], display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleMoveStep(stepIndex, 'up')}
              disabled={stepIndex === 0}
            >
              <ArrowUpward fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleMoveStep(stepIndex, 'down')}
              disabled={stepIndex === chapter.steps.length - 1}
            >
              <ArrowDownward fontSize="small" />
            </IconButton>
          </Box>
          <StepEditor
            step={step}
            stepIndex={stepIndex}
            onUpdate={(updates) => handleUpdateStep(stepIndex, updates)}
            onDelete={() => handleDeleteStep(stepIndex)}
            canDelete={chapter.steps.length > 1}
          />
        </Box>
      ))}
    </Box>
  )
}

export default ChapterEditor
